import { ThemeSchemaResponse } from '@api/api/theme-overview';
import { ExpandableText } from '@design/components/expandable-text/ExpandableText';
import { Tooltip, TooltipTrigger } from '@design/components/tooltip/Tooltip';
import semanticColorStyles from '@design/styles/SemanticColor.module.scss';
import { getPriceStatus, PriceStatus } from '@helpers/utils/numbers/numbers';
import { FollowThemeButton } from '@terminal/components/follow-theme-button/FollowThemeButton';
import { config } from '@terminal/config/app.config';
import { WebOnlyFeat } from '@terminal/config/WebOnlyFeat';
import { useNavigateToThemeOverview } from '@terminal/hooks/use-navigate-to-theme-overview/useNavigateToThemeOverview';
import type { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { formatNumber } from '@terminal/views/dashboard/calendar-widget/calendar-event-drawer/drawer-content/utils/number-formatter';
import { ThemeLogoIcon } from '@terminal/views/theme-overview/components/theme-logo-icon/ThemeLogoIcon';
import { useThemePerformanceReturn } from '@terminal/views/theme-overview/theme-overview-drawer/components/theme-performance-section/hooks/useThemePerformanceReturn';
import { SkeletonLoader } from '@toggle/design-system/src/components/skeleton-loader/SkeletonLoader';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import styles from './ThemeDrawerHeader.module.scss';

const THEME_LOGO_SIZE = 44;

export interface ThemeDrawerHeaderProps {
  theme: ThemeSchemaResponse;
  source: PosthogTrackingSource;
}

export const ThemeDrawerHeader = ({
  theme,
  source,
}: ThemeDrawerHeaderProps) => {
  const { t } = useTranslation(['widget']);

  const { navigateToTheme } = useNavigateToThemeOverview();
  const { getReturnForHorizon, isLoading } = useThemePerformanceReturn({
    themeId: theme.id,
    themeType: theme.type,
  });

  const isTeamsApp = config.app.isTeamsApp;
  const overviewLabel = t('widget:assetOverview.openThemeOverview');

  const handleOpenThemeOverview = () => {
    navigateToTheme({
      themeId: theme.id,
      source,
      replace: true,
    });
  };

  const returnValue = getReturnForHorizon('1D');
  const returnStatus = getPriceStatus(returnValue);
  const formattedReturn = formatNumber({
    isPercentage: true,
    value: returnValue ?? undefined,
  });

  return (
    <header className={styles.root} data-testid="theme-drawer-header">
      <div
        className={clsx(styles.header, {
          [styles.notInteractive]: isTeamsApp,
        })}
      >
        <WebOnlyFeat>
          <Tooltip
            inPortal
            isTouchDevice={false}
            label={overviewLabel}
            trigger={[TooltipTrigger.Hover, TooltipTrigger.Focus]}
          >
            <button
              aria-label={overviewLabel}
              className={styles.overviewButton}
              onClick={handleOpenThemeOverview}
              type="button"
            />
          </Tooltip>
        </WebOnlyFeat>
        <div className={styles.identity}>
          <div className={styles.logo}>
            <ThemeLogoIcon
              logo_url={theme.logo_url}
              name={theme.name}
              size={THEME_LOGO_SIZE}
              type={theme.type}
            />
          </div>
          <div className={styles.details}>
            <div className={styles.nameRow}>
              <span className={styles.name} data-testid="theme-name">
                {theme.name}
              </span>
              <div className={styles.action}>
                <FollowThemeButton themeId={theme.id} variant="icon" />
              </div>
            </div>
            {isLoading ? (
              <SkeletonLoader
                areas={[
                  {
                    styles: {
                      width: '100%',
                      height: '20px',
                    },
                  },
                ]}
              />
            ) : (
              <span
                className={clsx(styles.performance, {
                  [semanticColorStyles.directionNeutralDefault]:
                    returnStatus === PriceStatus.Default,
                  [semanticColorStyles.directionNegative]:
                    returnStatus === PriceStatus.Negative,
                  [semanticColorStyles.directionPositive]:
                    returnStatus === PriceStatus.Positive,
                })}
                data-testid="theme-intraday-price"
              >
                {formattedReturn}
              </span>
            )}
          </div>
        </div>
      </div>
      {theme.description && (
        <ExpandableText className={styles.description} maxLines={4}>
          {theme.description}
        </ExpandableText>
      )}
    </header>
  );
};
